import type {
  AuthCredentials,
  AuthData,
  JwtCredentials,
  JwtProviderParams,
  Token2FAEnrollment,
} from '@src/corelib/auth/providers';
import { JwtAuthProvider } from '@src/corelib/auth/providers';
import { useJwtStorage } from '@src/corelib/auth/storages';
import type { AuthenticatedUser, AuthenticatedUserDto } from '@src/types';
import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useRef } from 'react';

export type AuthenticationContextValue = {
  login: (username: string, password: string) => Promise<AuthCredentials>;
  logout: () => Promise<AuthCredentials>;
  verifyToken: () => Promise<AuthCredentials>;
  verifyTwoFactor: (challengeToken: string | null, otpCode: string) => Promise<JwtCredentials | null>;
  startTwoFactorEnrollment: (token: string) => Promise<Token2FAEnrollment>
  refreshToken: (loginAsToken?: string) => Promise<JwtCredentials>;
  authenticate: (data: AuthData) => AuthCredentials;
  unauthenticate: () => AuthCredentials;
  credentials: JwtCredentials | null;
  isAuthenticated: boolean;
  user: AuthenticatedUser | null;
};

const AuthenticationContext =
  React.createContext<AuthenticationContextValue | null>(null);

function createAuthProvider(params: JwtProviderParams) {
  return new JwtAuthProvider(params);
}

function AuthenticationProvider({ children }: PropsWithChildren<object>) {
  const storage = useJwtStorage();
  const provider = useRef(
    createAuthProvider({ storage, credentials: storage.credentials }),
  );

  const login = useCallback(
    (username: string, password: string) =>
      provider.current.login(username, password),
    [],
  );
  const verifyTwoFactor = useCallback(
    (challengeToken: string | null, otpCode: string) =>
      provider.current.verifyTwoFactor(challengeToken, otpCode),
    [],
  );
  const startTwoFactorEnrollment = useCallback(
    (token: string) => provider.current.startTwoFactorEnrollment(token),
    [],
  );

  const logout = useCallback(() => provider.current.logout(), []);
  const verifyToken = useCallback(() => provider.current.verifyToken(), []);
  const refreshToken = useCallback((loginAsToken?: string) => provider.current.refreshToken(loginAsToken), []);
  const authenticate = useCallback(
    (data: AuthData) => provider.current.authenticate(data),
    [],
  );
  const unauthenticate = useCallback(
    () => provider.current.unauthenticate(),
    [],
  );

  const contextValue = useMemo(
    () => ({
      login,
      logout,
      verifyToken,
      refreshToken,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      authenticate,
      unauthenticate,
      credentials: storage.credentials,
      isAuthenticated: storage.isAuthenticated,
      user: authenticatedUserFromDto(storage.credentials?.user),
    }),
    [
      authenticate,
      login,
      logout,
      refreshToken,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      storage.credentials,
      storage.isAuthenticated,
      unauthenticate,
      verifyToken,
    ],
  );

  return (
    <AuthenticationContext.Provider value={contextValue}>
      {children}
    </AuthenticationContext.Provider>
  );
}
AuthenticationProvider.displayName = 'AuthenticationProvider';

function authenticatedUserFromDto(
  user: AuthenticatedUserDto,
): AuthenticatedUser | null {
  if (user) {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      firstName: user.user_first_name,
      familyName: user.user_family_name,
      language: user.language,
      managerId: user.user_manager,
    };
  }
  return null;
}

export { AuthenticationContext, AuthenticationProvider };
